import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { useTheme } from '../lib/theme';
import {
  listRules, createRule, updateRule, deleteRule, getRulesStats, getCategories,
} from '../lib/api';

export default function AutoRules() {
  const { colors } = useTheme();
  const [rules, setRules] = useState([]);
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lst, st, cats] = await Promise.all([
        listRules(),
        getRulesStats(),
        getCategories(),
      ]);
      setRules(lst.items);
      setStats(st);
      setCategories(cats.categories.filter((c) => !c.is_special));
    } catch (err) {
      console.error('Failed to load rules:', err);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onToggle = async (rule) => {
    try {
      await updateRule(rule.id, { is_active: !rule.is_active });
      load();
    } catch (err) { console.error(err); }
  };

  const onDelete = async (rule) => {
    if (!window.confirm(`Delete rule for ${rule.vendor_pattern}?`)) return;
    try { await deleteRule(rule.id); load(); }
    catch (err) { console.error(err); }
  };

  return (
    <div className="rounded-xl border overflow-hidden"
      style={{ backgroundColor: colors.surface, borderColor: colors.border }}
      data-testid="auto-rules">
      <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: colors.border }}>
        <div>
          <p className="text-sm font-semibold" style={{ color: colors.text }}>Auto-categorization rules</p>
          {stats ? (
            <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>
              {stats.active_rules} active · {stats.receipts_auto_categorized_last_30d} receipts auto-categorized (30d)
            </p>
          ) : null}
        </div>
        <button
          data-testid="rules-add-btn"
          onClick={() => setAddOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
          style={{ backgroundColor: colors.accent }}
        >
          <Plus size={14} /> Add rule
        </button>
      </div>

      {loading ? (
        <div className="py-8 flex justify-center">
          <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: colors.border, borderTopColor: colors.accent }} />
        </div>
      ) : rules.length === 0 ? (
        <p className="p-5 text-sm text-center" style={{ color: colors.textMuted }}>
          No rules yet. Categorize 3+ receipts from the same vendor and we&apos;ll prompt you to create one automatically.
        </p>
      ) : (
        <div className="divide-y" style={{ borderColor: colors.border }}>
          {rules.map((r) => (
            <div key={r.id} data-testid={`rule-row-${r.id}`}
              className="flex items-center gap-3 px-5 py-3"
              style={{ borderBottom: `1px solid ${colors.border}` }}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate" style={{ color: colors.text }}>
                  {r.vendor_pattern} → {r.category_name}
                </p>
                <p className="text-[11px]" style={{ color: colors.textMuted }}>
                  {r.hit_count} auto-applied
                </p>
              </div>
              <label className="inline-flex items-center gap-1.5 cursor-pointer" data-testid={`rule-toggle-${r.id}`}>
                <input type="checkbox" checked={r.is_active} onChange={() => onToggle(r)} />
                <span className="text-xs" style={{ color: r.is_active ? colors.accent : colors.textMuted }}>
                  {r.is_active ? 'Active' : 'Inactive'}
                </span>
              </label>
              <button data-testid={`rule-delete-${r.id}`}
                onClick={() => onDelete(r)}
                className="p-1.5 rounded hover:opacity-70" style={{ color: colors.error }}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {addOpen ? (
        <AddRuleModal
          colors={colors}
          categories={categories}
          onClose={() => setAddOpen(false)}
          onCreated={() => { setAddOpen(false); load(); }}
        />
      ) : null}
    </div>
  );
}

function AddRuleModal({ colors, categories, onClose, onCreated }) {
  const [vendor, setVendor] = useState('');
  const [category, setCategory] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!vendor.trim() || !category || saving) return;
    setSaving(true); setError(null);
    try {
      await createRule(vendor.trim(), category);
      onCreated();
    } catch (e) { setError(e?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl overflow-hidden"
        style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
        data-testid="add-rule-modal">
        <div className="px-5 py-3 border-b" style={{ borderColor: colors.border }}>
          <h2 className="text-sm font-bold" style={{ color: colors.text }}>New auto-categorization rule</h2>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: colors.textMuted }}>
              Vendor (exact match, case-insensitive)
            </p>
            <input data-testid="rule-vendor-input"
              value={vendor} onChange={(e) => setVendor(e.target.value)}
              placeholder="e.g. STARBUCKS"
              className="w-full px-3 py-2 rounded-lg text-sm outline-none"
              style={{ backgroundColor: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }} />
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: colors.textMuted }}>
              Category
            </p>
            <select data-testid="rule-category-select"
              value={category} onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm outline-none"
              style={{ backgroundColor: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }}>
              <option value="">— pick a category —</option>
              {categories.map((c) => <option key={c.id} value={c.name}>{c.name} ({c.group})</option>)}
            </select>
          </div>
          {error ? <p className="text-xs" style={{ color: colors.error }}>{error}</p> : null}
          <div className="flex gap-2 pt-2">
            <button onClick={onClose}
              className="flex-1 py-2 rounded-lg text-sm font-semibold border"
              style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.textMuted }}>
              Cancel
            </button>
            <button data-testid="rule-submit-btn"
              onClick={submit} disabled={!vendor.trim() || !category || saving}
              className="flex-1 py-2 rounded-lg text-sm font-bold text-white"
              style={{ backgroundColor: colors.accent, opacity: (!vendor.trim() || !category || saving) ? 0.5 : 1 }}>
              {saving ? '…' : 'Create'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
